import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { noop, isEmpty } from 'lodash';
import Dropzone from 'react-dropzone';
import Webcam from 'react-webcam';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import { withStyles } from '@material-ui/core/styles';
import UploadIcon from '@material-ui/icons/CloudUpload';
import CameraIcon from '@material-ui/icons/CameraEnhance';
import CloseIcon from '@material-ui/icons/Close';
import RotateRightIcon from '@material-ui/icons/RotateRight';
import LinearProgress from '@material-ui/core/LinearProgress';
import CircularProgress from '@material-ui/core/CircularProgress';

import { injectIntl } from 'components';
import { isMobile } from 'helpers/detect-mobile';

const styles = theme => ({
  uploadButton: {
    backgroundColor: '#1976d2',
    color: 'white',
  },
  removeButton: {
    backgroundColor: '#ff602e',
    color: 'white',
  },
  dialog: {
    width: '100%',
    [theme.breakpoints.down('xs')]: {
      marginLeft: '0 !important',
    },
  },
});
const width = 300;
const height = 300;

// https://stackoverflow.com/questions/20600800/js-client-side-exif-orientation-rotate-and-mirror-jpeg-images

function rotateOrientation(srcBase64) {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const { width, height } = img;
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      canvas.width = height;
      canvas.height = width;
      ctx.setTransform(0, 1, -1, 0, height, 0);

      // draw image
      ctx.drawImage(img, 0, 0);

      // export base64
      resolve(canvas.toDataURL());
    };
    img.onerror = reject;

    img.crossOrigin = 'anonymous';
    img.src = srcBase64;
  });
}

class UploadImageDialog extends Component {
  static propTypes = {
    classes: PropTypes.shape({}).isRequired,
    image: PropTypes.string,
    isBanner: PropTypes.bool.isRequired,
    open: PropTypes.bool,
    requesting: PropTypes.bool,
    formatMessage: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    onUpload: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
  };

  static defaultProps = {
    image: '',
    open: false,
    requesting: false,
  };

  constructor(props) {
    super(props);

    this.initialState = {
      cameraOn: false,
      fileResult: '',
      filetype: '',
      loading: false,
    };
    this.state = this.initialState;
  }

  componentWillReceiveProps(nextProps) {
    if (!this.props.open && nextProps.open) {
      // Reset the state whenever modal opens
      this.setState({ ...this.initialState });
    }
  }

  onDrop = (files) => {
    if (isEmpty(files)) return;

    const file = files[0];
    const reader = new FileReader();

    this.setState({ filetype: file.type });

    reader.onload = () => {
      const fileResult = reader.result;
      this.setState({ fileResult });
    };

    reader.readAsDataURL(file);
  }

  handleUpload = () => {
    const { filetype, fileResult } = this.state;
    const imageBase64 = fileResult.split(',')[1];
    this.setState({ loading: true });

    this.props.onUpload({
      filetype,
      imageBase64,
    });
  }

  /*
  dataURItoBlob = (dataURI) => {
    const byteString = atob(dataURI.split(',')[1]);
    // separate out the mime component
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

    // write the bytes of the string to an ArrayBuffer
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);

    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mimeString });
  }
  */

  handleCameraAction = () => {
    const { cameraOn } = this.state;
    if (!cameraOn) {
      this.setState({ cameraOn: true });
    } else {
      const cameraShot = this.webcam.getScreenshot();
      this.setState({
        filetype: 'image/png',
        fileResult: cameraShot,
        cameraOn: false,
      });
    }
  }

  handleRemove = () => {
    const { onRemove } = this.props;

    onRemove();
  }

  handleRotate = () => {
    const { image } = this.props;
    const { fileResult } = this.state;

    rotateOrientation(fileResult || image).then((newBlob) => {
      this.setState({
        fileResult: newBlob,
      });
    });
  }

  render() {
    const {
      classes,
      image,
      isBanner,
      open,
      requesting,
      formatMessage,
      onClose,
    } = this.props;

    const {
      cameraOn,
      fileResult,
      loading,
    } = this.state;
    const preview = fileResult || image;

    const actions = [
      <Button
        color="primary"
        disabled={requesting}
        key="cancel"
        onClick={onClose}
        variant="text"
      >
        {formatMessage('Cancel')}
      </Button>,
      <Button
        key="remove"
        classes={{ root: classes.removeButton }}
        disabled={!image || requesting}
        onClick={this.handleRemove}
        variant="contained"
      >
        {formatMessage('Remove')}
      </Button>,
      <Button
        disabled={!fileResult || requesting}
        key="okay"
        onClick={this.handleUpload}
        classes={{ root: classes.uploadButton }}
        variant="contained"
      >
        {formatMessage('Upload')}
      </Button>,
    ];
    const cameraActions = [
      <Button
        key="upload-custom-button"
        onClick={() => { this.dropzoneRef.open(); }}
        style={{
          backgroundColor: '#1976d2',
          marginLeft: 10,
        }}
        variant="contained"
      >
        <UploadIcon style={{ color: 'white' }} />
      </Button>,
      !isMobile && (
        <Button
          key="camera-open-button"
          color="secondary"
          onClick={this.handleCameraAction}
          style={{ marginLeft: 10 }}
        >
          <CameraIcon
            style={{
              color: '#ff602e',
              transform: 'scale(1.5)',
            }} />
        </Button>
      ),
      cameraOn ? (
        <IconButton
          key="camera-close-button"
          onClick={() => this.setState({ cameraOn: false })}
        >
          <CloseIcon />
        </IconButton>
      ) : null,
      <IconButton
        disabled={!preview || requesting}
        key="rotate-button"
        onClick={this.handleRotate}
        variant="contained"
      >
        <RotateRightIcon />
      </IconButton>,
    ];

    if (!open) return null;

    return (
      <Dialog
        classes={{ paper: classes.dialog }}
        maxWidth={isBanner ? 'md' : 'sm'}
        onClose={requesting ? noop : onClose}
        open={open}
        scroll="body"
      >
        <DialogTitle>{formatMessage('Upload Picture')}</DialogTitle>
        <DialogContent classes={{ root: 'avatar-edit-dialog' }}>
          <Dropzone
            accept="image/jpeg, image/png"
            className={`dropzone ${isBanner ? 'landscape' : ''} ${requesting ? 'disabled' : ''}`}
            multiple={false}
            onDrop={this.onDrop}
            ref={(node) => { this.dropzoneRef = node; }}
          >
            {cameraOn ? (
              <Webcam
                audio={false}
                className="camera-feed"
                height={height}
                ref={(ref) => { this.webcam = ref; }}
                screenshotFormat="image/png"
                width={width}
              />
            ) : (
              <img
                alt=""
                className="picture-upload"
                src={preview}
              />
            )}
          </Dropzone>
          { fileResult && loading &&
            <div className="circular-progress">
              <CircularProgress />
            </div>
          }
          <div className="avatar-edit-tools">
            <div className="avatar-upload">
              {cameraActions}
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          {actions}
        </DialogActions>
      </Dialog>
    );
  }
}

const wrappedUploadImageDialog = withStyles(styles)(injectIntl(UploadImageDialog));
wrappedUploadImageDialog.displayName = 'UploadImageDialog';

export default wrappedUploadImageDialog;
